"use client";

import { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Select from "react-select";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { NumericFormat } from "react-number-format";
import AlertDialog from "./AlertDialog";

const API_BASE = "https://erp-production-72da01c8e651.herokuapp.com/api";

const customStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: "#374151",
    borderColor: state.isFocused ? "#f97316" : "#4b5563",
    color: "#e5e7eb",
    boxShadow: "none",
    outline: "none",
    "&:hover": { borderColor: "#f97316" },
    minHeight: "32px",
    fontSize: "12px",
    ...(state.isDisabled && {
      backgroundColor: "#4b5563",
      opacity: 0.5,
      cursor: "not-allowed",
    }),
  }),
  input: (provided) => ({
    ...provided,
    color: "#e5e7eb",
    outline: "none",
    boxShadow: "none",
    border: "none",
    margin: 0,
    padding: 0,
  }),
  singleValue: (provided) => ({
    ...provided,
    color: "#e5e7eb",
    fontSize: "12px",
  }),
  multiValue: (provided) => ({
    ...provided,
    backgroundColor: "#4b5563",
    borderRadius: "4px",
  }),
  multiValueLabel: (provided) => ({
    ...provided,
    color: "#e5e7eb",
    fontSize: "11px",
    padding: "1px 4px",
  }),
  multiValueRemove: (provided) => ({
    ...provided,
    color: "#9ca3af",
    "&:hover": { backgroundColor: "#f97316", color: "#fff" },
  }),
  placeholder: (provided) => ({
    ...provided,
    color: "#9ca3af",
    fontSize: "12px",
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? "#4b5563" : "#374151",
    color: state.isSelected ? "#f97316" : "#e5e7eb",
    "&:hover": { backgroundColor: "#4b5563" },
    fontSize: "12px",
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: "#374151",
    borderRadius: "0.375rem",
    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
    zIndex: 50,
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: "0 4px",
    flexWrap: "wrap",
  }),
  indicatorsContainer: (provided) => ({ ...provided, padding: "0" }),
  dropdownIndicator: (provided) => ({ ...provided, padding: "2px" }),
  clearIndicator: (provided) => ({ ...provided, padding: "2px" }),
};

export default function StockOutOrderView() {
  const { id } = useParams();
  const navigate = useNavigate();

  // ─── Form / order state ──────────────────────────────────────────
  const [formData, setFormData] = useState({
    purchaseOrder: "",
    poNumber: "",
    materialOrderNumber: "",
    shippingMethod: "Delivery",
    deliveryInstructions: "",
    contact: [],
    fieldContact: [],
    deliveryContact: null,
    expectedDeliveryDate: "",
    stockOutReason: "",
    stockOutReasonCategory: "", // ADD THIS
  });

  // ─── Data ────────────────────────────────────────────────────────
  const [project, setProject] = useState(null);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [poItems, setPoItems] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [users, setUsers] = useState([]);

  // ─── Step ────────────────────────────────────────────────────────
  const [step, setStep] = useState("select"); // "select" | "vendor" | "confirm"

  // ─── Step 1: item selection ──────────────────────────────────────
  const [selectedLineItemIds, setSelectedLineItemIds] = useState(new Set());

  // ─── Step 2: source selection ────────────────────────────────────
  // selectedSource replaces selectedNewVendor for type-safety.
  // Shape: { type: "vendor", vendorId, vendorName }
  //      | { type: "inventory", inventoryItemId, warehouseId, warehouseName, warehouseCode }
  const [selectedSource, setSelectedSource] = useState(null);
  const [selectedNewVendor, setSelectedNewVendor] = useState(null); // kept for legacy compat
  const [vendorPricingResults, setVendorPricingResults] = useState([]);
  const [missingPricingItems, setMissingPricingItems] = useState([]);
  const [availableVendors, setAvailableVendors] = useState([]);
  const [availableInventory, setAvailableInventory] = useState([]); // NEW

  // ─── Step 3: quantities ──────────────────────────────────────────
  const [stockOutItems, setStockOutItems] = useState([]);

  // ─── Filters ─────────────────────────────────────────────────────
  const [filters, setFilters] = useState({
    conditionName: [],
    sectionNumber: [],
    bidArea: [],
    size: [],
    laborId: [],
  });
  const [searchQuery, setSearchQuery] = useState("");

  // ─── UI ──────────────────────────────────────────────────────────
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingVendors, setIsLoadingVendors] = useState(false);
  const [isLoadingPricing, setIsLoadingPricing] = useState(false);
  const [error, setError] = useState(null);
  const [dialog, setDialog] = useState({
    open: false,
    type: "success",
    title: "",
    message: "",
    onConfirm: () => {},
  });

  const getHeaders = useCallback(() => {
    const token = localStorage.getItem("token");
    return { Authorization: `Bearer ${token}` };
  }, []);

  // ─── Initial load ────────────────────────────────────────────────
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const headers = getHeaders();
        const [projectRes, posRes, vendorsRes, contactsRes, usersRes] =
          await Promise.all([
            axios.get(`${API_BASE}/projects/${id}`, { headers }),
            axios.get(`${API_BASE}/purchaseOrders2?project=${id}`, { headers }),
            axios.get(`${API_BASE}/accounts/vendors`, { headers }),
            axios.get(`${API_BASE}/contacts`, { headers }),
            axios.get(`${API_BASE}/users`, { headers }),
          ]);
        setProject(projectRes.data);
        setPurchaseOrders(posRes.data);
        setVendors(vendorsRes.data);
        setContacts(contactsRes.data);
        setUsers(usersRes.data);
      } catch (err) {
        setError(
          `Failed to load data: ${err.response?.data?.message || err.message}`,
        );
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [id]);

  // ─── Fetch PO items ──────────────────────────────────────────────
  useEffect(() => {
    const fetchPOItems = async () => {
      if (!formData.purchaseOrder) {
        setPoItems([]);
        return;
      }
      try {
        const headers = getHeaders();
        const netRes = await axios.get(
          `${API_BASE}/purchaseOrders2/${formData.purchaseOrder}/item-net-quantities?excludeClosed=true`,
          { headers },
        );
        const netItems = netRes.data.success ? netRes.data.items : [];

        const getVendorName = (vendorRef) => {
          if (!vendorRef) return "Unassigned";
          const vendorId =
            typeof vendorRef === "object" ? vendorRef._id : vendorRef;
          if (typeof vendorRef === "object" && vendorRef.companyName)
            return vendorRef.companyName;
          const vendor = vendors.find((v) => v._id === vendorId);
          return vendor?.companyName || "Unassigned";
        };

        const items = netItems
          .filter((item) => !item.category || item.category === "M")
          .map((item) => ({
            ...item,
            assemblyId: item.assemblyId,
            lineItemId: item.lineItemId,
            vendorId: item.vendor?._id || item.vendor,
            vendorName: getVendorName(item.vendor),
            description: item.costCodeDescription || item.itemDescription || "",
            size: item.size || "-",
            bidArea: item.bidArea || "",
            conditionName: item.conditionName || "Uncategorized",
            sectionNumber: item.sectionNumber || "-",
            coveragePerUnit: item.coveragePerUnit || 1,
            uom: item.uom || item.container || "EA",
            price: item.price || item.latestPrice || 0,
            requiredQty: item.netRequiredQty || 0,
            qtyPending: item.qtyPending || 0,
            qtyOrdered: item.qtyOrdered || 0,
            qtyRemaining: item.remaining ?? item.netRemaining ?? 0,
            amount: item.amount || 0,
            unitPrice:
              item.netRequiredQty > 0
                ? (item.amount || 0) / item.netRequiredQty
                : 0,
          }))
          .filter((item) => item.qtyRemaining > 0)
          .sort((a, b) =>
            (a.conditionName || "").localeCompare(b.conditionName || ""),
          );

        setPoItems(items);
      } catch (err) {
        console.error("Error fetching net quantities for Stock-Out:", err);
        setError("Failed to load PO items. Please try again.");
        setPoItems([]);
      }
    };
    fetchPOItems();
  }, [formData.purchaseOrder, vendors]);

  // ─── Derived data ─────────────────────────────────────────────────
  const filteredItems = useMemo(() => {
    return poItems.filter((item) => {
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        if (
          !(item.description || "").toLowerCase().includes(q) &&
          !(item.sectionNumber || "").toLowerCase().includes(q) &&
          !(item.laborId || "").toLowerCase().includes(q)
        )
          return false;
      }
      if (
        filters.conditionName.length > 0 &&
        !filters.conditionName.includes(item.conditionName)
      )
        return false;
      if (
        filters.sectionNumber.length > 0 &&
        !filters.sectionNumber.includes(item.sectionNumber)
      )
        return false;
      if (filters.bidArea.length > 0 && !filters.bidArea.includes(item.bidArea))
        return false;
      if (filters.size.length > 0 && !filters.size.includes(item.size))
        return false;
      if (filters.laborId.length > 0 && !filters.laborId.includes(item.laborId))
        return false;
      return true;
    });
  }, [poItems, searchQuery, filters]);

  const filterOptions = useMemo(() => {
    const conditions = new Set(),
      sections = new Set(),
      bidAreas = new Set(),
      sizes = new Set(),
      laborIds = new Set();
    poItems.forEach((item) => {
      if (item.conditionName) conditions.add(item.conditionName);
      if (item.sectionNumber) sections.add(item.sectionNumber);
      if (item.bidArea) bidAreas.add(item.bidArea);
      if (item.size && item.size !== "-") sizes.add(item.size);
      if (item.laborId && item.laborId !== "-") laborIds.add(item.laborId);
    });
    const toOpts = (set) =>
      Array.from(set)
        .sort()
        .map((v) => ({ value: v, label: v }));
    return {
      conditionName: toOpts(conditions),
      sectionNumber: toOpts(sections),
      bidArea: toOpts(bidAreas),
      size: toOpts(sizes),
      laborId: toOpts(laborIds),
    };
  }, [poItems]);

  const groupedItems = useMemo(() => {
    const groups = {};
    filteredItems.forEach((item) => {
      const condition = item.conditionName || "Uncategorized";
      if (!groups[condition]) groups[condition] = [];
      groups[condition].push(item);
    });
    return groups;
  }, [filteredItems]);

  const selectedItems = useMemo(
    () =>
      poItems.filter((item) =>
        selectedLineItemIds.has(`${item.assemblyId}-${item.lineItemId}`),
      ),
    [poItems, selectedLineItemIds],
  );

  const itemTotals = useMemo(() => {
    const totals = new Map();
    selectedItems.forEach((si) => {
      const key = `${si.description}|${si.size}`;
      if (totals.has(key)) {
        const existing = totals.get(key);
        existing.count += 1;
        existing.totalRemaining += si.qtyRemaining || 0;
      } else {
        totals.set(key, {
          description: si.description,
          size: si.size,
          vendorName: si.vendorName,
          unitPrice: si.unitPrice || 0,
          count: 1,
          totalRemaining: si.qtyRemaining || 0,
          bidArea: si.bidArea,
          conditionName: si.conditionName,
        });
      }
    });
    return Array.from(totals.values()).sort((a, b) =>
      a.description.localeCompare(b.description),
    );
  }, [selectedItems]);

  const poOptions = useMemo(
    () =>
      purchaseOrders.map((po) => ({
        value: po._id,
        label: `${po.title} (PO# ${po.poNumber})`,
        poNumber: po.poNumber,
      })),
    [purchaseOrders],
  );

  const contactOptions = useMemo(
    () =>
      contacts
        .filter((c) =>
          selectedNewVendor ? c.relatedAccount === selectedNewVendor : true,
        )
        .map((c) => ({
          value: c._id,
          label: `${c.firstName} ${c.lastName} (${c.email || "No email"})`,
        })),
    [contacts, selectedNewVendor],
  );

  const userOptions = useMemo(
    () =>
      users.map((u) => ({
        value: u._id,
        label: `${u.displayName || "Unnamed"} (${u.phoneNumber || "No phone"})`,
      })),
    [users],
  );

  const totalCost = useMemo(
    () =>
      stockOutItems.reduce(
        (sum, si) =>
          sum +
          (si.quantity || 0) * (si.coveragePerUnit || 1) * (si.newPrice || 0),
        0,
      ),
    [stockOutItems],
  );

  // ─── PO change ───────────────────────────────────────────────────
  const handlePOChange = useCallback(
    async (selected) => {
      const poId = selected ? selected.value : "";
      try {
        let moNumber = "";
        if (poId) {
          const res = await axios.get(
            `${API_BASE}/materialOrders2/next-number?projectId=${id}`,
            { headers: getHeaders() },
          );
          moNumber = `${res.data.nextSeq}`;
        }
        setFormData({
          purchaseOrder: poId,
          poNumber: selected?.poNumber?.toString() || "",
          materialOrderNumber: moNumber,
          shippingMethod: "Delivery",
          deliveryInstructions: "",
          contact: [],
          fieldContact: [],
          deliveryContact: null,
          expectedDeliveryDate: "",
          stockOutReason: "",
          stockOutReasonCategory: "", // ADD THIS
        });
        setSelectedLineItemIds(new Set());
        setSelectedSource(null);
        setSelectedNewVendor(null);
        setVendorPricingResults([]);
        setMissingPricingItems([]);
        setAvailableVendors([]);
        setAvailableInventory([]);
        setStockOutItems([]);
        setFilters({
          conditionName: [],
          sectionNumber: [],
          bidArea: [],
          size: [],
          laborId: [],
        });
        setSearchQuery("");
        setStep("select");
      } catch (err) {
        setError(`Failed to change PO: ${err.message}`);
      }
    },
    [id, getHeaders],
  );

  // ─── Item toggle ─────────────────────────────────────────────────
  const toggleItemSelection = useCallback((item) => {
    const key = `${item.assemblyId}-${item.lineItemId}`;
    setSelectedLineItemIds((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  // ─── Step 2: find vendors + inventory for selected items ─────────
  const handleStockOutClick = useCallback(async () => {
    if (selectedItems.length === 0) {
      setDialog({
        open: true,
        type: "error",
        title: "No Items Selected",
        message: "Please select at least one item to stock out.",
        onConfirm: () => setDialog((d) => ({ ...d, open: false })),
      });
      return;
    }

    setIsLoadingVendors(true);
    try {
      const res = await axios.post(
        `${API_BASE}/stock-out-orders/find-vendors`,
        {
          lineItems: selectedItems.map((item) => ({
            lineItemId: item.lineItemId,
            description: item.description,
            currentVendorId: item.vendorId,
          })),
        },
        { headers: getHeaders() },
      );

      const results = res.data.results || [];

      // ── Build vendor list (unchanged) ──────────────────────────
      const vendorMap = new Map();
      results.forEach((r) => {
        if (r.matched && r.vendors) {
          r.vendors.forEach((v) => {
            if (!vendorMap.has(v.vendorId)) {
              vendorMap.set(v.vendorId, {
                vendorId: v.vendorId,
                vendorName: v.vendorName,
                itemCount: 0,
                totalItems: selectedItems.length,
              });
            }
            vendorMap.get(v.vendorId).itemCount++;
          });
        }
      });
      const vendorList = Array.from(vendorMap.values()).sort(
        (a, b) => b.itemCount - a.itemCount,
      );
      setAvailableVendors(vendorList);

      // ── Build inventory list (NEW) ─────────────────────────────
      const inventoryMap = new Map();
      results.forEach((r) => {
        if (r.matched && r.inventorySources) {
          r.inventorySources.forEach((inv) => {
            if (!inventoryMap.has(inv.inventoryItemId)) {
              inventoryMap.set(inv.inventoryItemId, {
                ...inv,
                itemCount: 0,
                totalItems: selectedItems.length,
              });
            }
            inventoryMap.get(inv.inventoryItemId).itemCount++;
          });
        }
      });
      const inventoryList = Array.from(inventoryMap.values()).sort(
        (a, b) => b.itemCount - a.itemCount,
      );
      setAvailableInventory(inventoryList);

      // Warn about unmatched items
      const unmatched = results.filter((r) => !r.matched);
      if (unmatched.length > 0) {
        setDialog({
          open: true,
          type: "warning",
          title: "Items Missing From Database",
          message: `${unmatched.length} item(s) were not found in the Item Database:\n\n${unmatched
            .map((u) => `• ${u.description}`)
            .join(
              "\n",
            )}\n\nPlease add these items to the Item Database with vendor pricing before creating a stock-out order.`,
          onConfirm: () => setDialog((d) => ({ ...d, open: false })),
        });
        if (unmatched.length === selectedItems.length) {
          setIsLoadingVendors(false);
          return;
        }
      }

      setStep("vendor");
    } catch (err) {
      setError(`Failed to find vendors: ${err.message}`);
    } finally {
      setIsLoadingVendors(false);
    }
  }, [selectedItems, getHeaders]);

  // ─── Step 2b: select a source (vendor OR inventory) ─────────────
  const handleSourceSelect = useCallback(
    async (source) => {
      // source: { type: "vendor", vendorId, vendorName }
      //       | { type: "inventory", inventoryItemId, warehouseId, warehouseName, warehouseCode, ... }
      const isInventory = source.type === "inventory";
      const resolvedId = isInventory ? source.inventoryItemId : source.vendorId;

      setSelectedSource(source);
      setSelectedNewVendor(isInventory ? null : source.vendorId);
      setIsLoadingPricing(true);

      try {
        const res = await axios.post(
          `${API_BASE}/stock-out-orders/vendor-pricing`,
          {
            vendorId: resolvedId, // inventoryItemId reused as vendorId for inventory path
            sourceType: source.type, // "vendor" | "inventory"
            lineItems: selectedItems.map((item) => ({
              lineItemId: item.lineItemId,
              description: item.description,
              size: item.size || "",
              uom: item.uom || "",
            })),
          },
          { headers: getHeaders() },
        );

        const pricing = res.data.results || [];
        const missing = res.data.missingItems || [];

        setVendorPricingResults(pricing);
        setMissingPricingItems(missing);

        if (missing.length > 0) {
          setDialog({
            open: true,
            type: "warning",
            title: "Missing Vendor Pricing",
            message: `${missing.length} item(s) don't have pricing from this source:\n\n${missing
              .map((m) => `• ${m.description}: ${m.reason}`)
              .join(
                "\n",
              )}\n\nPlease add pricing in the Item Database first, or select a different source.`,
            onConfirm: () => setDialog((d) => ({ ...d, open: false })),
          });
        }

        // Build stock-out items — same shape as before, plus inventory flags
        const soItems = pricing.map((p) => {
          const sourceItem = selectedItems.find(
            (si) => si.lineItemId === p.lineItemId,
          );
          const coverage = p.coveragePerUnit || 1;
          const vendorRate = p.price || 0;

          const originalCoverage = sourceItem?.coveragePerUnit || coverage;
          let originalRate = 0;
          if (sourceItem?.price && sourceItem.price > 0) {
            originalRate = sourceItem.price;
          } else if (sourceItem?.unitPrice && originalCoverage > 0) {
            originalRate = sourceItem.unitPrice / originalCoverage;
          }

          return {
            assemblyId: sourceItem?.assemblyId || "",
            lineItemId: p.lineItemId,
            description: p.description,
            itemCode: p.itemCode,
            size: p.size || sourceItem?.size || "",
            uom: p.uom || sourceItem?.uom || "EA",
            bidArea: sourceItem?.bidArea || "",
            conditionName: sourceItem?.conditionName || "",
            sectionNumber: sourceItem?.sectionNumber || "",
            originalVendorId: sourceItem?.vendorId || "",
            originalVendorName: sourceItem?.vendorName || "",
            originalPrice: originalRate,
            originalRate: originalRate,
            originalExtended: originalRate * originalCoverage,
            newPrice: vendorRate,
            newExtended: p.extendedUnitPrice || vendorRate * coverage,
            maxQty: sourceItem?.qtyRemaining || 0,
            quantity: 0,
            coveragePerUnit: coverage,
            // Inventory-specific — false / null for vendor items
            isInventory: !!p.isInventory,
            inventoryItemId: p.inventoryItemId || null,
            warehouseId: p.warehouseId || source.warehouseId || null,
            warehouseName: p.warehouseName || source.warehouseName || "",
            warehouseCode: p.warehouseCode || source.warehouseCode || "",
            quantityAvailable: p.quantityAvailable || null,
          };
        });

        setStockOutItems(soItems);
        if (missing.length === 0) setStep("confirm");
      } catch (err) {
        setError(`Failed to load pricing: ${err.message}`);
      } finally {
        setIsLoadingPricing(false);
      }
    },
    [selectedItems, getHeaders],
  );

  // ─── Step 3: quantity change ─────────────────────────────────────
  const handleQtyChange = useCallback((lineItemId, value) => {
    setStockOutItems((prev) =>
      prev.map((si) => {
        if (si.lineItemId !== lineItemId) return si;
        const qty = Math.min(Math.max(0, parseInt(value) || 0), si.maxQty);
        return { ...si, quantity: qty };
      }),
    );
  }, []);

  // ─── Submit ──────────────────────────────────────────────────────
  const handleSubmit = async () => {
    const itemsWithQty = stockOutItems.filter((si) => si.quantity > 0);

    if (itemsWithQty.length === 0) {
      setDialog({
        open: true,
        type: "error",
        title: "No Quantities Set",
        message: "Please set a quantity greater than 0 for at least one item.",
        onConfirm: () => setDialog((d) => ({ ...d, open: false })),
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const isInventorySource = selectedSource?.type === "inventory";
      const firstItem = itemsWithQty[0];

      const orderData = {
        project: id,
        purchaseOrder: formData.purchaseOrder,
        originalVendor: firstItem.originalVendorId || null,
        newVendor: isInventorySource ? null : selectedNewVendor,
        poNumber: formData.poNumber,
        shippingMethod: formData.shippingMethod,
        deliveryInstructions: formData.deliveryInstructions,
        contact: formData.contact,
        fieldContact: formData.fieldContact,
        deliveryContact: formData.deliveryContact,
        expectedDeliveryDate: formData.expectedDeliveryDate || null,
        stockOutReason: formData.stockOutReason,
        stockOutReasonCategory: formData.stockOutReasonCategory, // ADD THIS

        // Inventory order metadata
        isInventorySource,
        warehouseId: selectedSource?.warehouseId || null,
        warehouseName: selectedSource?.warehouseName || "",
        items: itemsWithQty.map((si) => ({
          assembly: si.assemblyId,
          lineItemId: si.lineItemId,
          quantity: si.quantity,
          size: si.size || "",
          bidArea: si.bidArea || "",
          newPrice: si.newPrice,
          originalPrice: si.originalPrice,
          coveragePerUnit: si.coveragePerUnit || 1,
          isInventorySource: si.isInventory || isInventorySource,
          inventoryItemId:
            si.inventoryItemId || selectedSource?.inventoryItemId || null,
        })),
      };

      await axios.post(`${API_BASE}/stock-out-orders`, orderData, {
        headers: getHeaders(),
      });

      setDialog({
        open: true,
        type: "success",
        title: "Stock-Out Order Created",
        message: isInventorySource
          ? `Stock-out order ${formData.materialOrderNumber} created from warehouse inventory. Items are now pending.`
          : `Stock-out order ${formData.materialOrderNumber} was created successfully. Items are now pending.`,
        confirmText: "OK",
        onConfirm: () => {
          setDialog((d) => ({ ...d, open: false }));
          navigate(`/Projects/${id}`, { state: { initialTab: "Materials" } });
        },
      });
    } catch (err) {
      setDialog({
        open: true,
        type: "error",
        title: "Failed to Create Stock-Out Order",
        message:
          err.response?.data?.message ||
          err.message ||
          "An unexpected error occurred.",
        onConfirm: () => setDialog((d) => ({ ...d, open: false })),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const showConfirmationDialog = (e) => {
    e.preventDefault();
    setDialog({
      open: true,
      type: "warning",
      title: "Confirm Stock-Out Order",
      message:
        "This will reserve the selected quantities on the original items (pending). The vendor swap will complete upon delivery. Continue?",
      confirmText: "Create Stock-Out Order",
      cancelText: "Cancel",
      onConfirm: async () => {
        setDialog((d) => ({
          ...d,
          message: "Creating stock-out order... Please wait.",
          confirmText: "Creating...",
          cancelText: "",
        }));
        await handleSubmit();
      },
      onClose: () => setDialog((d) => ({ ...d, open: false })),
      onCancel: () => setDialog((d) => ({ ...d, open: false })),
    });
  };

  // ─── Helpers ─────────────────────────────────────────────────────
  const formatCurrency = (num) =>
    Number(num || 0).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-gray-200">Loading...</div>
      </div>
    );
  }

  return (
    <div className="ml-4 mx-auto px-4 py-6">
      <AlertDialog
        open={dialog.open}
        type={dialog.type}
        title={dialog.title}
        message={dialog.message}
        confirmText={dialog.confirmText || "Confirm"}
        cancelText={dialog.cancelText || "Cancel"}
        onClose={() => setDialog((d) => ({ ...d, open: false }))}
        onConfirm={dialog.onConfirm}
      />

      {/* ── Header ── */}
      <div className="mb-6 shadow-xl bg-gray-700 rounded-lg p-6 border border-gray-600">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-semibold text-white">
              Create Stock-Out Order
            </h1>
            <p className="text-sm text-gray-400 mt-1">
              {step === "select" &&
                "Step 1: Select a PO and choose items to stock out."}
              {step === "vendor" && "Step 2: Select the source to order from."}
              {step === "confirm" &&
                "Step 3: Set quantities and confirm the order."}
            </p>
          </div>
          <div className="flex space-x-4">
            {step !== "select" && (
              <button
                onClick={() => {
                  if (step === "confirm") setStep("vendor");
                  else setStep("select");
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-gray-600 rounded-md hover:bg-gray-500"
              >
                Back
              </button>
            )}
            <button
              onClick={() =>
                navigate(`/Projects/${id}`, {
                  state: { initialTab: "Materials" },
                })
              }
              className="px-4 py-2 text-sm font-medium text-white bg-gray-600 rounded-md hover:bg-gray-500"
            >
              Cancel
            </button>
          </div>
        </div>

        <div className="mt-4 flex space-x-2">
          {["select", "vendor", "confirm"].map((s, i) => (
            <div
              key={s}
              className={`flex items-center space-x-2 px-3 py-1 rounded-full text-xs font-medium ${
                step === s
                  ? "bg-orange-500/30 text-orange-400 border border-orange-500"
                  : i < ["select", "vendor", "confirm"].indexOf(step)
                    ? "bg-green-500/20 text-green-400 border border-green-500/50"
                    : "bg-gray-600 text-gray-400 border border-gray-500"
              }`}
            >
              <span>{i + 1}</span>
              <span className="capitalize">
                {s === "confirm"
                  ? "Confirm & Create"
                  : s === "select"
                    ? "Select Items"
                    : "Choose Source"}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════ */}
      {/* STEP 1: SELECT PO AND ITEMS                                   */}
      {/* ══════════════════════════════════════════════════════════════ */}
      {step === "select" && (
        <div className="space-y-6">
          <div className="bg-gray-700 p-6 rounded-lg border border-gray-600 shadow-xl">
            <h2 className="text-lg font-semibold text-white mb-4">
              Order Details
            </h2>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div>
                <label className="block text-sm font-medium text-gray-400">
                  Purchase Order *
                </label>
                <Select
                  options={poOptions}
                  value={
                    poOptions.find((o) => o.value === formData.purchaseOrder) ||
                    null
                  }
                  onChange={handlePOChange}
                  placeholder="Select Purchase Order..."
                  className="mt-1"
                  styles={customStyles}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400">
                  MO Number
                </label>
                <input
                  type="text"
                  value={formData.materialOrderNumber}
                  readOnly
                  className="mt-1 block w-full rounded-md border-gray-500 bg-gray-600 text-gray-200 shadow-sm sm:text-sm"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-400">
                  Reason for Stock-Out
                </label>
                <div className="flex flex-col gap-2 mt-1">
                  <select
                    value={formData.stockOutReasonCategory}
                    onChange={(e) => {
                      const val = e.target.value;
                      setFormData((f) => ({
                        ...f,
                        stockOutReasonCategory: val,
                        stockOutReason: val !== "Other" ? "" : f.stockOutReason,
                      }));
                    }}
                    className="block w-full rounded-md border-gray-500 bg-gray-600 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                  >
                    <option value="">— Select reason —</option>
                    <option value="Vendor Out of Stock">
                      Vendor Out of Stock
                    </option>
                    <option value="Better Pricing Available">
                      Better Pricing Available
                    </option>
                    <option value="Lead Time Too Long">
                      Lead Time Too Long
                    </option>
                    <option value="Site Condition Change">
                      Site Condition Change
                    </option>
                    <option value="Foreman Field Decision">
                      Foreman Field Decision
                    </option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gray-700 p-6 rounded-lg border border-gray-600 shadow-xl">
            <h2 className="text-lg font-semibold text-white mb-4">
              Select Items to Stock Out
            </h2>

            {formData.purchaseOrder && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-400">
                      Search
                    </label>
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search description..."
                      className="mt-1 w-full rounded-md border-gray-500 bg-gray-600 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                    />
                  </div>
                  {[
                    "conditionName",
                    "sectionNumber",
                    "bidArea",
                    "size",
                    "laborId",
                  ].map((field) => (
                    <div key={field}>
                      <label className="block text-sm font-medium text-gray-400 capitalize">
                        {field === "conditionName"
                          ? "Condition"
                          : field === "sectionNumber"
                            ? "Section"
                            : field === "bidArea"
                              ? "Bid Area"
                              : field === "laborId"
                                ? "Labor ID"
                                : "Size"}
                      </label>
                      <Select
                        options={filterOptions[field]}
                        value={filterOptions[field].filter((o) =>
                          filters[field].includes(o.value),
                        )}
                        onChange={(opts) =>
                          setFilters((f) => ({
                            ...f,
                            [field]: opts ? opts.map((o) => o.value) : [],
                          }))
                        }
                        placeholder={`All...`}
                        isMulti
                        isClearable
                        className="mt-1"
                        styles={customStyles}
                      />
                    </div>
                  ))}
                </div>

                <div className="mb-4 flex items-center space-x-4">
                  <button
                    type="button"
                    onClick={() => {
                      setFilters({
                        conditionName: [],
                        sectionNumber: [],
                        bidArea: [],
                        size: [],
                        laborId: [],
                      });
                      setSearchQuery("");
                    }}
                    className="px-4 py-2 text-sm font-medium text-white bg-gray-600 rounded-md hover:bg-gray-500 transition-colors"
                  >
                    Clear Filters
                  </button>
                  <span className="text-gray-400 text-sm">
                    {filteredItems.length} items shown •{" "}
                    {selectedLineItemIds.size} selected
                  </span>
                </div>

                {selectedItems.length > 0 && (
                  <div className="mb-4 bg-gray-800 rounded-lg border border-gray-600 p-4">
                    <div className="flex justify-between items-center mb-3">
                      <h3 className="text-sm font-semibold text-orange-400">
                        Selected Items Summary
                      </h3>
                      <span className="text-xs text-gray-400">
                        {itemTotals.length} unique item
                        {itemTotals.length !== 1 ? "s" : ""} •{" "}
                        {selectedItems.length} assembly line
                        {selectedItems.length !== 1 ? "s" : ""}
                      </span>
                    </div>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {itemTotals.map((item, idx) => (
                        <div
                          key={idx}
                          className="flex justify-between items-center py-2 px-3 bg-gray-700 rounded text-sm border border-gray-600"
                        >
                          <div className="flex-1 min-w-0">
                            <p
                              className="text-white truncate"
                              title={item.description}
                            >
                              {item.description}
                              {item.size && item.size !== "-"
                                ? ` — ${item.size}`
                                : ""}
                            </p>
                            <div className="flex items-center gap-3 mt-0.5">
                              {item.bidArea && (
                                <span className="text-xs text-gray-500">
                                  Area: {item.bidArea}
                                </span>
                              )}
                              <span className="text-xs text-gray-500">
                                Vendor: {item.vendorName}
                              </span>
                              {item.count > 1 && (
                                <span className="text-xs text-blue-400">
                                  ({item.count} assemblies)
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center space-x-4 ml-4">
                            <div className="text-right">
                              <p className="text-blue-400 font-medium">
                                {item.totalRemaining} avail
                              </p>
                              <p className="text-xs text-gray-400">
                                @{" "}
                                <NumericFormat
                                  value={item.unitPrice}
                                  displayType="text"
                                  thousandSeparator=","
                                  decimalScale={2}
                                  fixedDecimalScale
                                  prefix="$"
                                />{" "}
                                ea
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}

            {!formData.purchaseOrder ? (
              <p className="text-gray-400">
                Select a Purchase Order to see available items.
              </p>
            ) : filteredItems.length === 0 ? (
              <p className="text-gray-400">No material items available.</p>
            ) : (
              <div className="space-y-4">
                {Object.entries(groupedItems).map(([conditionName, items]) => (
                  <div
                    key={conditionName}
                    className="border border-gray-600 rounded-lg overflow-hidden"
                  >
                    <div className="bg-gray-800 px-4 py-2 flex justify-between items-center">
                      <span className="text-white font-medium">
                        {conditionName}
                      </span>
                      <span className="text-gray-400 text-sm">
                        {items.length} items
                      </span>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-600 bg-gray-700">
                        <thead className="bg-gray-750">
                          <tr>
                            <th className="px-2 py-2 text-left text-xs font-medium text-gray-400 uppercase w-10"></th>
                            <th className="px-2 py-2 text-left text-xs font-medium text-gray-400 uppercase">
                              Section
                            </th>
                            <th className="px-2 py-2 text-left text-xs font-medium text-gray-400 uppercase">
                              Description
                            </th>
                            <th className="px-2 py-2 text-left text-xs font-medium text-gray-400 uppercase">
                              Size
                            </th>
                            <th className="px-2 py-2 text-left text-xs font-medium text-gray-400 uppercase">
                              Vendor
                            </th>
                            <th className="px-2 py-2 text-right text-xs font-medium text-gray-400 uppercase">
                              Avail
                            </th>
                            <th className="px-2 py-2 text-right text-xs font-medium text-gray-400 uppercase">
                              Unit Price
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-600">
                          {items.map((item) => {
                            const key = `${item.assemblyId}-${item.lineItemId}`;
                            const selected = selectedLineItemIds.has(key);
                            return (
                              <tr
                                key={key}
                                onClick={() => toggleItemSelection(item)}
                                className={`cursor-pointer ${selected ? "bg-orange-500/20" : "hover:bg-gray-600"}`}
                              >
                                <td className="px-2 py-2 text-sm">
                                  <input
                                    type="checkbox"
                                    checked={selected}
                                    onChange={() => toggleItemSelection(item)}
                                    className="h-4 w-4 text-orange-400 focus:ring-orange-500 border-gray-500 rounded"
                                  />
                                </td>
                                <td className="px-2 py-2 text-sm text-white">
                                  {item.sectionNumber}
                                </td>
                                <td className="px-2 py-3 text-sm text-gray-200 max-w-[250px]">
                                  {item.description}
                                </td>
                                <td className="px-2 py-2 text-sm text-white">
                                  {item.size}
                                </td>
                                <td className="px-2 py-2 text-sm text-white">
                                  {item.vendorName}
                                </td>
                                <td className="px-2 py-2 text-sm text-blue-400 text-right font-medium">
                                  {item.qtyRemaining}
                                </td>
                                <td className="px-2 py-2 text-sm text-right text-white">
                                  ${formatCurrency(item.unitPrice)}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {selectedLineItemIds.size > 0 && (
              <div className="mt-4 flex justify-end">
                <button
                  type="button"
                  onClick={handleStockOutClick}
                  disabled={isLoadingVendors}
                  className="px-6 py-2 text-sm font-medium text-white bg-orange-500 rounded-md hover:bg-orange-600 disabled:opacity-50"
                >
                  {isLoadingVendors
                    ? "Finding Sources..."
                    : `Stock Out ${selectedLineItemIds.size} Item(s) →`}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════ */}
      {/* STEP 2: SELECT SOURCE (vendor OR inventory warehouse)          */}
      {/* ══════════════════════════════════════════════════════════════ */}
      {step === "vendor" && (
        <div className="bg-gray-700 p-6 rounded-lg border border-gray-600 shadow-xl">
          <h2 className="text-lg font-semibold text-white mb-2">
            Select Source
          </h2>
          <p className="text-sm text-gray-400 mb-4">
            Warehouse inventory is shown first — selecting it uses your existing
            stock with no vendor variance. Vendor options are listed below.
          </p>

          {availableInventory.length === 0 && availableVendors.length === 0 ? (
            <div className="text-yellow-400 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
              No alternate vendors or inventory stock found for the selected
              items. Please add vendor pricing or inventory stock to the Item
              Database first.
            </div>
          ) : (
            <div className="space-y-3">
              {/* ── Inventory sources ── */}
              {availableInventory.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                    From Warehouse Inventory
                  </p>
                  <div className="space-y-2">
                    {availableInventory.map((inv) => {
                      const isSelected =
                        selectedSource?.type === "inventory" &&
                        selectedSource.inventoryItemId === inv.inventoryItemId;
                      return (
                        <div
                          key={inv.inventoryItemId}
                          onClick={() =>
                            handleSourceSelect({ type: "inventory", ...inv })
                          }
                          className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                            isSelected
                              ? "border-green-500 bg-green-500/10"
                              : "border-green-700/40 bg-green-900/10 hover:border-green-600 hover:bg-green-900/20"
                          }`}
                        >
                          <div className="flex justify-between items-center">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-lg bg-green-500/20 border border-green-500/30 flex items-center justify-center shrink-0">
                                <svg
                                  className="w-4 h-4 text-green-400"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-2 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                                  />
                                </svg>
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <span className="text-white font-medium">
                                    {inv.warehouseName}
                                  </span>
                                  {inv.warehouseCode && (
                                    <span className="px-1.5 py-0.5 text-[9px] font-mono bg-gray-700 text-gray-400 rounded border border-gray-600">
                                      {inv.warehouseCode}
                                    </span>
                                  )}
                                  <span className="px-2 py-0.5 text-[10px] font-semibold bg-green-500/20 text-green-300 border border-green-500/30 rounded">
                                    From Inventory
                                  </span>
                                </div>
                                <p className="text-xs text-green-400 mt-0.5">
                                  {inv.quantityAvailable} units available · no
                                  vendor variance created
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              {isLoadingPricing && isSelected && (
                                <span className="text-gray-400 text-sm">
                                  Loading...
                                </span>
                              )}
                              <span
                                className={`text-sm ${inv.itemCount === inv.totalItems ? "text-green-400" : "text-yellow-400"}`}
                              >
                                {inv.itemCount}/{inv.totalItems} items matched
                              </span>
                              {inv.itemCount === inv.totalItems && (
                                <span className="text-green-400 text-xs font-medium bg-green-500/10 px-2 py-1 rounded">
                                  Full Match
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Divider when both exist */}
              {availableInventory.length > 0 && availableVendors.length > 0 && (
                <div className="flex items-center gap-3 py-1">
                  <div className="flex-1 border-t border-gray-600" />
                  <span className="text-xs text-gray-500">
                    or order from a vendor
                  </span>
                  <div className="flex-1 border-t border-gray-600" />
                </div>
              )}

              {/* ── Vendor sources ── */}
              {availableVendors.length > 0 && (
                <div>
                  {availableInventory.length > 0 && (
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                      From Vendor
                    </p>
                  )}
                  <div className="space-y-2">
                    {availableVendors.map((v) => {
                      const isSelected =
                        selectedSource?.type === "vendor" &&
                        selectedSource.vendorId === v.vendorId;
                      return (
                        <div
                          key={v.vendorId}
                          onClick={() =>
                            handleSourceSelect({
                              type: "vendor",
                              vendorId: v.vendorId,
                              vendorName: v.vendorName,
                            })
                          }
                          className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                            isSelected
                              ? "border-orange-500 bg-orange-500/10"
                              : "border-gray-600 hover:border-gray-500 hover:bg-gray-600"
                          }`}
                        >
                          <div className="flex justify-between items-center">
                            <div>
                              <span className="text-white font-medium">
                                {v.vendorName}
                              </span>
                              <span
                                className={`ml-3 text-sm ${v.itemCount === v.totalItems ? "text-green-400" : "text-yellow-400"}`}
                              >
                                {v.itemCount}/{v.totalItems} items available
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              {isLoadingPricing && isSelected && (
                                <span className="text-gray-400 text-sm">
                                  Loading pricing...
                                </span>
                              )}
                              {v.itemCount === v.totalItems && (
                                <span className="text-green-400 text-xs font-medium bg-green-500/10 px-2 py-1 rounded">
                                  Full Match
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Missing items warning */}
          {missingPricingItems.length > 0 && (
            <div className="mt-4 p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
              <h3 className="text-red-400 font-medium mb-2">Missing Pricing</h3>
              <p className="text-sm text-gray-300 mb-2">
                The following items don't have pricing from this source. Add
                their pricing in the Item Database first, or select a different
                source.
              </p>
              <ul className="list-disc list-inside text-sm text-gray-400 space-y-1">
                {missingPricingItems.map((m, i) => (
                  <li key={i}>
                    {m.description} —{" "}
                    <span className="text-red-400">{m.reason}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {vendorPricingResults.length > 0 &&
            missingPricingItems.length === 0 && (
              <div className="mt-4 flex justify-end">
                <button
                  type="button"
                  onClick={() => setStep("confirm")}
                  className="px-6 py-2 text-sm font-medium text-white bg-orange-500 rounded-md hover:bg-orange-600"
                >
                  Continue with {vendorPricingResults.length} Item(s) →
                </button>
              </div>
            )}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════ */}
      {/* STEP 3: SET QUANTITIES AND CONFIRM                            */}
      {/* ══════════════════════════════════════════════════════════════ */}
      {step === "confirm" && (
        <form onSubmit={showConfirmationDialog} className="space-y-6">
          {/* Shipping / Contact Details */}
          <div className="bg-gray-700 p-6 rounded-lg border border-gray-600 shadow-xl">
            <h2 className="text-lg font-semibold text-white mb-4">
              Shipping & Contacts
            </h2>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div>
                <label className="block text-sm font-medium text-gray-400">
                  Shipping Method
                </label>
                <select
                  value={formData.shippingMethod}
                  onChange={(e) =>
                    setFormData((f) => ({
                      ...f,
                      shippingMethod: e.target.value,
                    }))
                  }
                  className="mt-1 block w-full rounded-md border-gray-500 bg-gray-600 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                >
                  <option value="Delivery">Delivery</option>
                  <option value="Pickup">Pickup</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400">
                  Expected Delivery Date
                </label>
                <input
                  type="date"
                  value={formData.expectedDeliveryDate}
                  onChange={(e) =>
                    setFormData((f) => ({
                      ...f,
                      expectedDeliveryDate: e.target.value,
                    }))
                  }
                  className="mt-1 block w-full rounded-md border-gray-500 bg-gray-600 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400">
                  Vendor Contacts
                </label>
                <Select
                  options={contactOptions}
                  value={contactOptions.filter((o) =>
                    formData.contact.includes(o.value),
                  )}
                  onChange={(opts) =>
                    setFormData((f) => ({
                      ...f,
                      contact: opts ? opts.map((o) => o.value) : [],
                    }))
                  }
                  placeholder="Select Contacts..."
                  className="mt-1"
                  styles={customStyles}
                  isMulti
                />
              </div>
            </div>
            <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
              {formData.shippingMethod === "Pickup" && (
                <div>
                  <label className="block text-sm font-medium text-gray-400">
                    Delivery Driver
                  </label>
                  <Select
                    options={userOptions}
                    value={
                      userOptions.find(
                        (o) => o.value === formData.deliveryContact,
                      ) || null
                    }
                    onChange={(opt) =>
                      setFormData((f) => ({
                        ...f,
                        deliveryContact: opt?.value || null,
                      }))
                    }
                    placeholder="Select Driver..."
                    className="mt-1"
                    styles={customStyles}
                    isClearable
                  />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-400">
                  Field Contacts
                </label>
                <Select
                  options={userOptions}
                  value={userOptions.filter((o) =>
                    formData.fieldContact.includes(o.value),
                  )}
                  onChange={(opts) =>
                    setFormData((f) => ({
                      ...f,
                      fieldContact: opts ? opts.map((o) => o.value) : [],
                    }))
                  }
                  placeholder="Select Field Contacts..."
                  className="mt-1"
                  styles={customStyles}
                  isMulti
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400">
                  Delivery Instructions
                </label>
                <input
                  type="text"
                  value={formData.deliveryInstructions}
                  onChange={(e) =>
                    setFormData((f) => ({
                      ...f,
                      deliveryInstructions: e.target.value,
                    }))
                  }
                  placeholder="Enter delivery instructions..."
                  className="mt-1 block w-full rounded-md border-gray-500 bg-gray-600 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                />
              </div>
            </div>
          </div>

          {/* Items table */}
          <div className="bg-gray-700 p-6 rounded-lg border border-gray-600 shadow-xl">
            <h2 className="text-lg font-semibold text-white mb-2">
              Stock-Out Items
            </h2>
            <p className="text-sm text-gray-400 mb-4">
              {selectedSource?.type === "inventory" ? (
                <>
                  Source:{" "}
                  <span className="text-green-400 font-medium">
                    {selectedSource.warehouseName} (Warehouse Inventory)
                  </span>{" "}
                  — no variance budget will be created
                </>
              ) : (
                <>
                  New vendor:{" "}
                  <span className="text-orange-400 font-medium">
                    {vendors.find((v) => v._id === selectedNewVendor)
                      ?.companyName || "Unknown"}
                  </span>
                </>
              )}
            </p>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-600 bg-gray-700">
                <thead className="bg-gray-800">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-400 uppercase">
                      Description
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-400 uppercase">
                      Size
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-400 uppercase">
                      UOM
                    </th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-400 uppercase">
                      Cvg
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-400 uppercase">
                      Source
                    </th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-400 uppercase">
                      Orig Rate
                    </th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-400 uppercase">
                      New Rate
                    </th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-400 uppercase">
                      Diff/Unit
                    </th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-400 uppercase">
                      Max Qty
                    </th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-400 uppercase">
                      Qty to Order
                    </th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-400 uppercase">
                      Line Total
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-600">
                  {stockOutItems.map((item) => {
                    const coverage = item.coveragePerUnit || 1;
                    const isCoverageItem = coverage > 1;
                    const rateDiff =
                      (item.newPrice || 0) - (item.originalRate || 0);
                    const lineTotal =
                      (item.quantity || 0) * coverage * (item.newPrice || 0);

                    return (
                      <tr
                        key={item.lineItemId}
                        className={`hover:bg-gray-600 ${item.isInventory ? "bg-green-900/5" : ""}`}
                      >
                        <td className="px-3 py-2 text-sm text-gray-200 max-w-[200px]">
                          {item.description}
                        </td>

                        {/* Size — locked for inventory items */}
                        <td className="px-3 py-2 text-sm text-white">
                          {item.size || "-"}
                          {item.isInventory && (
                            <span className="ml-1 text-[9px] text-gray-500">
                              🔒
                            </span>
                          )}
                        </td>

                        <td className="px-3 py-2 text-sm text-gray-400">
                          {item.uom || "EA"}
                        </td>
                        <td className="px-3 py-2 text-sm text-right text-gray-400">
                          {isCoverageItem ? coverage.toFixed(2) : "1"}
                        </td>

                        {/* Source column — warehouse badge OR vendor name */}
                        <td className="px-3 py-2 text-sm">
                          {item.isInventory ? (
                            <div>
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold bg-green-500/20 text-green-300 border border-green-500/30 rounded">
                                <svg
                                  className="w-3 h-3"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16"
                                  />
                                </svg>
                                {item.warehouseName || "Warehouse"}
                              </span>
                              {item.quantityAvailable != null && (
                                <div className="text-[10px] text-gray-500 mt-0.5">
                                  {item.quantityAvailable} in stock
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="text-gray-400">
                              {item.originalVendorName}
                            </span>
                          )}
                        </td>

                        <td className="px-3 py-2 text-sm text-right text-gray-400">
                          <div>${formatCurrency(item.originalRate || 0)}</div>
                          {isCoverageItem && (
                            <div className="text-xs text-gray-500">
                              /{item.uom}
                            </div>
                          )}
                        </td>

                        {/* New Rate — locked (read-only) for inventory items */}
                        <td className="px-3 py-2 text-sm text-right">
                          {item.isInventory ? (
                            <div>
                              <span className="text-green-300 font-medium">
                                ${formatCurrency(item.newPrice || 0)}
                              </span>
                              {isCoverageItem && (
                                <div className="text-xs text-gray-500">
                                  /{item.uom} · locked
                                </div>
                              )}
                            </div>
                          ) : (
                            <div>
                              <span className="text-white font-medium">
                                ${formatCurrency(item.newPrice || 0)}
                              </span>
                              {isCoverageItem && (
                                <div className="text-xs text-gray-500">
                                  /{item.uom}
                                </div>
                              )}
                            </div>
                          )}
                        </td>

                        {/* Diff — zero / hidden for inventory (same cost) */}
                        <td
                          className={`px-3 py-2 text-sm text-right font-medium ${
                            item.isInventory
                              ? "text-gray-500"
                              : rateDiff > 0.001
                                ? "text-red-400"
                                : rateDiff < -0.001
                                  ? "text-green-400"
                                  : "text-gray-400"
                          }`}
                        >
                          {item.isInventory
                            ? "—"
                            : `${rateDiff > 0 ? "+" : ""}$${formatCurrency(rateDiff)}`}
                        </td>

                        <td className="px-3 py-2 text-sm text-right text-blue-400">
                          {item.maxQty}
                        </td>

                        <td className="px-3 py-2 text-sm text-right">
                          <NumericFormat
                            value={item.quantity}
                            onValueChange={(values) =>
                              handleQtyChange(
                                item.lineItemId,
                                values.floatValue ?? 0,
                              )
                            }
                            thousandSeparator=","
                            decimalScale={0}
                            allowNegative={false}
                            isAllowed={(values) => {
                              const val = values.floatValue ?? 0;
                              return val >= 0 && val <= item.maxQty;
                            }}
                            className={`w-20 text-right rounded border px-2.5 py-1.5 text-sm
                              focus:border-orange-500 focus:ring-orange-500/40 focus:outline-none
                              hover:border-gray-400
                              [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none
                              [&::-webkit-inner-spin-button]:appearance-none
                              ${
                                item.quantity > item.maxQty
                                  ? "border-red-600 bg-red-900/30 text-red-200"
                                  : "border-gray-600 bg-gray-700 text-white"
                              }`}
                          />
                        </td>

                        <td className="px-3 py-2 text-sm text-right text-white">
                          <NumericFormat
                            value={lineTotal}
                            displayType="text"
                            thousandSeparator=","
                            decimalScale={2}
                            fixedDecimalScale
                            prefix="$"
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Total */}
            <div className="mt-4 p-4 bg-gray-800 rounded-lg flex justify-between items-center">
              <span className="text-gray-200 font-medium">Total Cost:</span>
              <NumericFormat
                value={totalCost}
                displayType="text"
                thousandSeparator=","
                decimalScale={2}
                fixedDecimalScale
                prefix="$"
                className="text-lg font-semibold text-orange-400"
              />
            </div>

            {/* Actions */}
            <div className="mt-4 flex justify-end space-x-4">
              <button
                type="button"
                onClick={() => setStep("vendor")}
                className="px-4 py-2 text-sm font-medium text-gray-200 bg-gray-600 rounded-md hover:bg-gray-500"
              >
                Back
              </button>
              <button
                type="submit"
                disabled={
                  isSubmitting || stockOutItems.every((si) => si.quantity <= 0)
                }
                className={`px-6 py-2 text-sm font-medium text-white rounded-md ${
                  isSubmitting || stockOutItems.every((si) => si.quantity <= 0)
                    ? "bg-orange-300 cursor-not-allowed"
                    : "bg-orange-500 hover:bg-orange-600"
                }`}
              >
                {isSubmitting ? "Creating..." : "Create Stock-Out Order"}
              </button>
            </div>
          </div>
        </form>
      )}
    </div>
  );
}
